import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  ButtonBase,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../app/providers/AuthProvider";
import { useEmpresa } from "../../../app/providers/EmpresaProvider";
import Ilustracion from "../../../assets/Ilustracion.svg";

function ActionCard({ icon, title, description, onClick, disabled, ariaLabel }) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || title}
      sx={{
        width: "100%",
        minHeight: { xs: 180, md: 220 },
        borderRadius: "20px",
        textAlign: "left",
        alignItems: "stretch",
        justifyContent: "stretch",
        bgcolor: "rgba(255,255,255,.78)",
        border: "1px solid rgba(255,255,255,.92)",
        boxShadow: "0 8px 20px rgba(27,37,89,.08), inset 0 0 3px rgba(142,142,159,.18)",
        overflow: "hidden",
        position: "relative",
        transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&:hover": {
          transform: "translateY(-6px)",
          bgcolor: "rgba(248,250,255,.96)",
          borderColor: "rgba(35,78,190,.35)",
          boxShadow: "0 14px 30px rgba(27,37,89,.16), inset 0 0 3px rgba(142,142,159,.18)",
        },
        "&.Mui-disabled": { opacity: 0.68 },
      }}
    >
      <Stack sx={{ width: "100%", height: "100%", p: 2 }}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: "50%",
            bgcolor: "rgba(30,68,177,.10)",
            color: "primary.700",
            display: "grid",
            placeItems: "center",
            mb: 2,
            "& svg": { width: 31, height: 31 },
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: "primary.900", mb: 1 }}>
          {title}
        </Typography>
        <Typography sx={{ maxWidth: "92%", fontSize: 13, fontWeight: 500, color: "rgba(19,27,58,.82)", lineHeight: 1.25 }}>
          {description}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,.9)",
              color: "primary.800",
              border: "1px solid rgba(20,32,82,.08)",
              display: "grid",
              placeItems: "center",
            }}
          >
            {disabled ? <CircularProgress size={17} /> : <ArrowForwardIcon sx={{ fontSize: 23 }} />}
          </Box>
        </Box>
      </Stack>
    </ButtonBase>
  );
}

function ModuleSection({ title, children }) {
  return (
    <Stack
      spacing={1.5}
      sx={{
        p: 2,
        bgcolor: "rgba(244, 245, 251, 0.88)",
        borderRadius: "24px",
        border: "1px solid rgba(255,255,255,0.8)",
        boxShadow:
          "inset 0px 0px 5px rgba(117, 117, 138, 0.25), 0px 10px 28px rgba(27, 37, 89, 0.08)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "fit-content",
          px: 2,
          py: 1,
          bgcolor: "primary.500",
          borderRadius: "33px",
          boxShadow:
            "inset 0px 0px 3px rgba(192, 192, 201, 0.5), 0px 5px 14px rgba(16, 47, 148, 0.28)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.5,
        }}
      >
        <Typography sx={{ color: "logoblanco", fontSize: 18, fontWeight: 700, lineHeight: 1 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Stack>
  );
}

const cardsGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(3, minmax(0, 1fr))",
  },
  gap: 2,
  alignItems: "stretch",
};

export default function ConsultantHomePage() {
  const navigate = useNavigate();
  const { empresas, loading, selectContext, user } = useAuth();
  const { setEmpresaId, setEmpresaNombre, setSucursalId } = useEmpresa();
  const [enteringId, setEnteringId] = useState("");
  const [error, setError] = useState("");

  const licenses = useMemo(() => (Array.isArray(empresas) ? empresas : []), [empresas]);
  const displayName = String(user?.display_name || user?.displayName || user?.name || "Consultora").trim();

  const enterLicense = async (license) => {
    if (!license?.id || enteringId) return;

    setEnteringId(String(license.id));
    setError("");
    try {
      const result = await selectContext({ licenseId: Number(license.id) });
      setEmpresaId(String(result?.me?.context?.licenseId ?? license.id));
      setEmpresaNombre(license.name || "");
      setSucursalId(result?.me?.context?.branchId != null ? String(result.me.context.branchId) : "");
      navigate("/inicio");
    } catch (requestError) {
      setError(requestError?.message || "No se pudo ingresar a la licencia. Intentá nuevamente.");
    } finally {
      setEnteringId("");
    }
  };

  return (
    <Box p={2} sx={{ width: "100%", boxSizing: "border-box" }}>
      <Stack spacing={2}>
        <Box
          sx={{
            width: "100%",
            minHeight: { xs: 125, md: 112 },
            height: { xs: "auto", md: 112 },
            px: { xs: 2, sm: 3 },
            py: { xs: 2, md: 1.5 },
            borderRadius: "18px",
            overflow: "hidden",
            position: "relative",
            border: "1px solid rgba(96,96,113,.18)",
            boxShadow: "inset 0 0 4px rgba(117,117,138,.35), 0 10px 30px rgba(0,0,0,.06)",
            background: "linear-gradient(135deg, rgba(191,198,225,1) 0%, rgba(175,169,223,1) 55%, rgba(162,218,231,.9) 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack spacing={0.2} sx={{ position: "relative", zIndex: 1 }}>
            <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 600, color: "primary.900" }}>
              Bienvenido,
            </Typography>
            <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: "primary.900" }}>
              estás en el espacio de {displayName}
            </Typography>
          </Stack>
          <Box sx={{ height: 112, width: { sm: 190, lg: 220 }, display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "flex-end", ml: 2 }}>
            <Box component="img" src={Ilustracion} alt="Ilustración" sx={{ height: "120%", width: "100%", objectFit: "contain", my: "-10px" }} />
          </Box>
        </Box>

        <ModuleSection title="Licencias asignadas">
          {error ? <Alert severity="error" onClose={() => setError("")} sx={{ mb: 1.5 }}>{error}</Alert> : null}
          {loading ? (
            <Box role="status" aria-label="Cargando licencias" sx={cardsGridSx}>
              {[1, 2, 3].map((item) => <Skeleton key={item} variant="rounded" height={220} sx={{ borderRadius: "20px" }} />)}
            </Box>
          ) : licenses.length === 0 ? (
            <Box sx={{ py: 5, px: 2, textAlign: "center", borderRadius: "20px", bgcolor: "neutral.50" }}>
              <StorefrontIcon sx={{ fontSize: 40, color: "secondary.500", mb: 1 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 900, color: "primary.900" }}>No tenés licencias asignadas</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "neutral.700", mt: 0.5 }}>Solicitá una asignación para acceder al contexto de un cliente.</Typography>
            </Box>
          ) : (
            <Box sx={cardsGridSx}>
              {licenses.map((license) => (
                <ActionCard
                  key={license.id}
                  icon={<StorefrontIcon />}
                  title={license.name || `Licencia #${license.id}`}
                  description="Ingresá a los módulos, datos y operaciones de esta licencia."
                  onClick={() => enterLicense(license)}
                  disabled={Boolean(enteringId)}
                  ariaLabel={`Ingresar a ${license.name || `Licencia ${license.id}`}`}
                />
              ))}
            </Box>
          )}
        </ModuleSection>

        <ModuleSection title="Configuraciones generales">
          <Box sx={cardsGridSx}>
            <ActionCard
              icon={<Inventory2Icon />}
              title="Lista global de insumos"
              description="Consultá el catálogo base de insumos disponible para todas las licencias."
              onClick={() => navigate("/consultora/insumos-globales")}
            />
          </Box>
        </ModuleSection>
      </Stack>
    </Box>
  );
}
